<template>
  <DataTable
    :value="pricesList"
    class="overflow-hidden"
    tableStyle="min-width: 70rem"
    stripedRows
  >
    <template #header>
      <div class="flex items-center justify-between font-normal pb-3">
        <div>
          <h2 class="text-lg tracking-tight flex flex-row justify-center items-center">
            <Icon :icon="icon" class="text-xl mr-2" />
            {{ priceName }}
          </h2>
        </div>
      </div>
    </template>
    <Column v-if="showQualityColumn" field="quality" header="Quality" sortable :style="{ minWidth: '100px' }" :breakpoints="{ '960px': 'hidden' }">
      <template #body="slotProps">
        <template v-if="!isEditingRow(slotProps.data.id)">
          <RouterLink
              v-if="slotProps.data.quality"
              :to="`/productSpecification/${slotProps.data.quality}`"
              class="font-medium text-neutral-800 block hover:underline"
          >
            {{ getQualityName(slotProps.data.quality) }}
          </RouterLink>
          <span v-else class="text-neutral-400 italic">No quality selected</span>
        </template>

        <Dropdown
            v-else
            inputName="qualityId"
            id="qualityId"
            placeholder="Choose Quality"
            :options="qualities"
            :optionLabel="getQualityLabel()"
            optionValue="id"
            class="w-full"
            :class="isEditingRow(slotProps.data.id) ? '' : 'border-none'"
            :disabled="!isEditingRow(slotProps.data.id)"
            :showClear="props.sectionType === 'knitting'"
            label=""
            v-model="slotProps.data.quality"
        />
      </template>
    </Column>

    <Column field="priceTypesList" header="Price Type" sortable :style="{ minWidth: '100px' }">
      <template #body="slotProps">
        <Dropdown
          :options="filteredPriceTypeOptions"
          placeholder="select price type"
          optionLabel="priceTypeName"
          optionValue="priceTypeValue"
          id="priceType"
          class="w-full"
          :class="isEditingRow(slotProps.data.id) ? '' : 'border-none'"
          :disabled="!isEditingRow(slotProps.data.id) || props.sectionType === 'testing' || props.sectionType === 'qualityCheck'"
          v-model="slotProps.data.priceType"
        />
      </template>
    </Column>
    <Column field="costPerUnitAmount" :header="costPerUnitHeader" sortable :style="{ minWidth: '10rem' }">
      <template #body="slotProps">
        <span v-if="!isEditingRow(slotProps.data.id)">
          {{ CurrencyType.find(c => c.id === slotProps.data.costPerUnitCurrency)?.symbol }} {{ slotProps.data.costPerUnitAmount }}
        </span>
        <div v-else class="flex items-center gap-x-2 w-full">
          <Dropdown
            id="id"
            placeholder="Currency"
            :options="CurrencyType"
            optionLabel="symbol"
            optionValue="id"
            v-model="slotProps.data.costPerUnitCurrency"
            class="w-1/3"
            :class="isEditingRow(slotProps.data.id) ? '' : 'border-none'"
            :disabled="!isEditingRow(slotProps.data.id)"
          />
          <InputNumber
            v-model="slotProps.data.costPerUnitAmount"
            class="w-2/3 overflow-hidden"
            :class="isEditingRow(slotProps.data.id) ? '' : 'border-none'"
            :disabled="!isEditingRow(slotProps.data.id)"
            :min-fraction-digits="0"
            :max-fraction-digits="5"
            placeholder="Enter cost"
          />
        </div>
      </template>
    </Column>
    <Column field="startDate" header="Start Date" sortable :style="{ minWidth: '10rem' }">
      <template #body="slotProps">
        <p v-if="!isEditingRow(slotProps.data.id)" class="px-4">
          {{ formatedDate(slotProps.data.startDate) }}
        </p>
        <Calendar
          v-else
          v-model="slotProps.data.startDate"
          placeholder="Start date"
          class="!w-32 overflow-hidden"
          dateFormat="dd/mm/yy"
        />
      </template>
    </Column>
    <Column field="" header="">
      <template #body="slotProps" v-if="edit">
        <Button
          v-ripple
          outlined
          class="gap-x-2 w-1/8"
          @click="handleEnableEdit(slotProps.data.id)"
          :loading="loading"
          :disabled="false"
          v-if="!isEditingRow(slotProps.data.id) && !slotProps.data.isNew"
        >
          <span>Edit</span>
          <Icon icon="basil:edit-outline" class="text-2xl" />
        </Button>
        <div v-else class="flex gap-2">
          <Button
            v-ripple
            outlined
            class="w-1/8"
            @click="handleSave(slotProps.index, slotProps.data.id)"
            :loading="loading"
            :disabled="false"
            v-if="slotProps.data.isNew"
          >
            <span>Save</span>
            <Icon icon="basil:check-outline" class="text-xl" />
          </Button>
          <Button
            v-ripple
            outlined
            class="w-1/8"
            @click="handleUpdate(slotProps.index, slotProps.data.id)"
            :loading="loading"
            :disabled="false"
            v-else
          >
            <span>Update</span>
            <Icon icon="basil:refresh-outline" class="text-xl" />
          </Button>
        </div>
      </template>
    </Column>
    <Column field="" header="">
      <template #body="slotProps" v-if="edit">
        <div class="relative inline-block group/tooltip" v-if="!slotProps.data.isNew">
          <Button
            v-ripple
            outlined
            severity="danger"
            class="gap-x-2 w-1/8"
            @click="handleDelete(slotProps.data.id)"
            :loading="loading"
            :disabled="!slotProps.data.canBeDeleted"
            v-if="!isEditingRow(slotProps.data.id) && !slotProps.data.isNew"
          >
            <span>Delete</span>
            <Icon icon="basil:trash-outline" class="text-2xl" />
          </Button>

          <div
            v-if="!slotProps.data.canBeDeleted"
            class="absolute right-full top-1/2 transform -translate-y-1/2 -translate-x-8 opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-200 pointer-events-none z-50"
          >
            <div class="bg-gray-800 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap">
              Cannot delete pricing that has been used in batches, fire tests, or quality checks
            </div>
            <div class="absolute top-1/2 right-0 transform translate-x-full -translate-y-1/2 w-0 h-0 border-l-4 border-r-0 border-t-4 border-b-4 border-transparent border-l-gray-800"></div>
          </div>
        </div>
      </template>
    </Column>
  </DataTable>
  <div class="px-4 py-2">
    <Button
      v-ripple
      outlined
      class="gap-x-2"
      @click="addPricing()"
      :loading="loading"
      :disabled="isAddingNew"
      v-if="edit"
    >
      <span> Add New Pricing </span>
      <Icon icon="basil:plus-solid" class="text-xl" />
    </Button>
  </div>
</template>
<script lang="ts" setup>
import { computed } from 'vue'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import { onMounted, ref } from 'vue'
import { Icon } from '@iconify/vue'
import Dropdown from 'primevue/dropdown'
import InputGroup from 'primevue/inputgroup'
import Calendar from 'primevue/calendar'
import { getProductList } from '@/api/products'
import { CurrencyType } from '@/types/currency-types'
import { FabricStatusEnum } from '@/types/product-types'
import { formatedDate, generateUniqueId } from '@/constants'
import {PriceType, priceTypeOptions} from '@/constants/price-type'

const props = defineProps<{
  icon: string
  priceName: string
  list: {
    id: string
    quality: string
    priceType: number
    costPerUnitAmount: number
    costPerUnitCurrency: number
    startDate: Date
    endDate?: Date | null
    isActive?: boolean
    hasBeenUsed?: boolean
    canBeDeleted?: boolean
    isNew?: boolean
  }[]
  save?: (item: any) => void
  update?: (index: number, id: string) => void
  delete?: (id: string) => void
  edit?: Boolean
  sectionType?: string
}>()

const pricesList = ref(props.list)
const loading = ref(false)
const editingRow = ref(null)
const qualities = ref<any[]>([])
const isAddingNew = ref<boolean>(false)

const isEditingRow = (rowId: string) => editingRow.value === rowId

const costPerUnitHeader = computed(() => {
  return props.sectionType === 'qualityCheck' ? 'Cost per batch' : 'Cost per unit'
})

const addPricing = () => {
  const id = generateUniqueId()

  let defaultPriceType = 0;

  if (props.sectionType === 'testing') {
    defaultPriceType = PriceType.CostPerTest;
  } else if (props.sectionType === 'qualityCheck') {
    defaultPriceType = PriceType.CostPerCheck;
  }

  const newRow = {
    id,
    quality: '',
    priceType: defaultPriceType,
    costPerUnitCurrency: 0,
    costPerUnitAmount: 0,
    startDate: new Date(),
    isNew: true
  }
  pricesList.value.push({ ...newRow })
  handleEnableEdit(id)
  isAddingNew.value = true
}

const getQualityLabel = () => {
  if (qualities.value.length > 0) {
    console.log('First quality item:', qualities.value[0])
  }

  if (props.sectionType === 'knitting') {
    if (qualities.value.length > 0) {
      if (qualities.value[0].unfinishedReference) {
        return 'unfinishedReference'
      } else if (qualities.value[0].reference) {
        return 'reference'
      } else if (qualities.value[0].name) {
        return 'name'
      }
    }
  }

  return 'finishedReference'
}

const getQualityName = (qualityId: string) => {
  const quality = qualities.value.find(q => q.id === qualityId)

  if (!quality) return 'Unknown Quality'

  if (props.sectionType === 'knitting') {
    return quality.unfinishedReference || quality.reference || quality.name || 'Unknown Quality'
  }

  return quality.finishedReference || quality.reference || quality.name || 'Unknown Quality'
}

const handleEnableEdit = (id: any) => {
  editingRow.value = id
}

const handleSave = async (index: number, id: string) => {
  const itemIndex = pricesList.value.findIndex((item) => item.id === id);
  const item = pricesList.value[itemIndex];

  if (!item) {
    console.error("Item not found");
    return;
  }

  try {
    loading.value = true;
    await props.save?.(item);

    editingRow.value = null;
    isAddingNew.value = false;

    if (itemIndex !== -1) {
      pricesList.value[itemIndex].isNew = false;
      pricesList.value[itemIndex].canBeDeleted = true;
      pricesList.value = [...pricesList.value];
    }
  } catch (error) {
    console.error("Error saving:", error);
  } finally {
    loading.value = false;
  }
};

const handleUpdate = (index: number, id: string) => {
  editingRow.value = null
  if (props.update) {
    props.update(index, id)
  }
}

const handleDelete = async (id: string) => {
  if (props.delete) {
    try {
      loading.value = true
      await props.delete(id)

      const itemIndex = pricesList.value.findIndex((item) => item.id === id)
      if (itemIndex !== -1) {
        pricesList.value.splice(itemIndex, 1)
        pricesList.value = [...pricesList.value]
      }
    } catch (error) {
      console.error("Error deleting:", error)
    } finally {
      loading.value = false
    }
  }
}

const filteredPriceTypeOptions = computed(() => {
  if (props.sectionType === 'knitting' || props.sectionType === 'finishing') {
    return priceTypeOptions.filter(option => option.priceTypeValue !== PriceType.CostPerTest)
  }

  return priceTypeOptions
})

const showQualityColumn = computed(() => {
  return props.sectionType !== 'testing' && props.sectionType !== 'qualityCheck';
});

onMounted(async () => {
  const allProducts = await getProductList()
  if (props.sectionType === 'knitting') {
    qualities.value = allProducts.filter(
      (product) => product.fabricStatus === FabricStatusEnum.Unfinished
    )
  } else {
    qualities.value = allProducts.filter(
      (product) => product.fabricStatus === FabricStatusEnum.Finished
    )
  }
  props.list.forEach((item) => {
    item.startDate = new Date(item.startDate)
  })
})
</script>
